<template>
  <div class="user-dropdown">
    <section class="profile">
      <img
        v-if="authUser.profile_picture_url"
        :src="authUser.profile_picture_url"
        alt=""
        class="avatar"
      >
      <span v-else class="avatar" aria-hidden="true">{{ initials }}</span>
      <div class="identity">
        <p class="name">
          {{ authUser.first_name }} {{ authUser.last_name }}
        </p>
        <p class="email">
          {{ authUser.email }}
        </p>
        <p v-if="companyName" class="company">
          {{ companyName }}
        </p>
        <span v-if="roleLabel" class="role-badge">{{ roleLabel }}</span>
      </div>
    </section>

    <section v-if="hasWholeSaleCostPermission || isAdmin" class="toggles">
      <h2 class="section-label">
        Pricing on screen
      </h2>
      <toggle-switch
        v-if="hasWholeSaleCostPermission"
        :key="toggleResetKey"
        id="showWholesalePrice"
        class-name="menu-toggle"
        label="Wholesale price"
        :value="showWholesalePrice"
        @change="onToggleWholesalePrice"
      />
      <toggle-switch
        v-if="isAdmin"
        id="showMsrp"
        class-name="menu-toggle"
        label="MSRP"
        :value="showMsrp"
        :disabled="msrpSaving"
        @change="onToggleMsrp"
      />
    </section>

    <section v-if="isAdmin" class="toggles">
      <h2 class="section-label">
        Site header
      </h2>
      <toggle-switch
        id="megaMenuEnabled"
        class-name="menu-toggle"
        label="Mega menu"
        :value="megaMenuEnabled"
        :disabled="megaMenuSaving"
        @change="onToggleMegaMenu"
      />
    </section>

    <nav class="links" aria-label="Account">
      <ul>
        <li v-for="link in links" :key="link.name">
          <router-link :to="{ name: link.name }" @click.native="$emit('closeDropdown')">
            {{ link.label }}
          </router-link>
        </li>
      </ul>
    </nav>

    <div class="logout">
      <button type="button" @click="logoutFunction">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          aria-hidden="true"
        >
          <path d="M9 2h4.5v12H9M10 8H2M5 5L2 8l3 3" />
        </svg>
        Log out
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import ToggleSwitch from '~/components/core/Inputs/ToggleSwitch'
import { PERMISSIONS } from '~/constants'

// company_admin is shown as "Admin" in account UIs; the stored role stays company_admin.
const ROLE_LABELS = {
  admin: 'Admin',
  rep: 'Rep',
  company_admin: 'Admin',
  buyer: 'Buyer',
  associate: 'Associate'
}

export default {
  name: 'UserDropdown',
  components: { ToggleSwitch },
  data: () => ({
    toggleResetKey: 0,
    msrpSaving: false,
    megaMenuSaving: false
  }),
  computed: {
    ...mapGetters({
      authUser: 'auth/user',
      isAdmin: 'auth/isAdmin',
      permissions: 'auth/permissions',
      showWholesalePrice: 'auth/showWholesalePrice',
      showMsrp: 'settings/showMsrp',
      megaMenuEnabled: 'settings/megaMenuEnabled'
    }),
    hasWholeSaleCostPermission () {
      return this.permissions.includes(PERMISSIONS.WHOLESALE_COST)
    },
    initials () {
      const { first_name: first = '', last_name: last = '' } = this.authUser
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase()
    },
    roleLabel () {
      return ROLE_LABELS[this.authUser.role] || ''
    },
    companyName () {
      return this.isAdmin ? '' : this.authUser.companies?.[0]?.name || ''
    },
    links () {
      return [
        { name: 'dashboard', label: 'Dashboard' },
        this.permissions.includes(PERMISSIONS.ORDER_HISTORY) &&
          { name: 'order-history.index', label: 'Orders & invoices' },
        { name: 'settings', label: 'Account settings' }
      ].filter(Boolean)
    }
  },
  created () {
    this.fetchOrderSummary()
  },
  methods: {
    ...mapMutations({
      clearActiveCollection: 'catalog_products/CLEAR_ACTIVE_COLLECTION'
    }),
    ...mapActions({
      clearActiveDraftOrder: 'orders/clearActiveDraftOrder',
      fetchOrderSummary: 'dashboard/fetchOrderSummary',
      isOrderHasProductQuantities: 'orders/isOrderHasProductQuantities',
      logout: 'auth/logout',
      disableWholesalePrice: 'auth/disableWholesalePrice',
      updatePublicSettings: 'settings/updatePublicSettings'
    }),
    async logoutFunction () {
      await this.logout()
      this.clearActiveCollection()
      this.clearActiveDraftOrder()
      this.isOrderHasProductQuantities(false)
      this.$router.push({ name: 'login' })
    },
    // Turning prices OFF is instant; turning them ON opens the global password
    // modal (rendered at app root so it covers the full screen). The toggle is
    // remounted right after so it snaps back to reflect the store — it only
    // shows ON once the password unlocks prices (store getter flips).
    onToggleWholesalePrice (next) {
      if (next) {
        this.$modal.show('wholesale-price-confirm')
        this.$nextTick(() => { this.toggleResetKey++ })
      } else {
        this.disableWholesalePrice()
      }
    },
    // Global, admin-only MSRP visibility. Persists immediately for all users.
    async onToggleMsrp (next) {
      this.msrpSaving = true
      try {
        await this.updatePublicSettings({ show_msrp: next })
        this.$toast.success(next ? 'MSRP visible' : 'MSRP hidden')
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Failed to update MSRP setting')
      } finally {
        this.msrpSaving = false
      }
    },
    // Global, admin-only. Swaps the public header between the legacy markup and
    // the admin-configured mega menu. This toggle is the documented rollback for
    // that rollout, so it has to be reachable without a deploy.
    async onToggleMegaMenu (next) {
      this.megaMenuSaving = true
      try {
        await this.updatePublicSettings({ mega_menu_enabled: next })
        this.$toast.success(next ? 'Mega menu header enabled' : 'Mega menu header disabled')
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Failed to update mega menu setting')
      } finally {
        this.megaMenuSaving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$ud-text: #1a1a1a;
$ud-muted: #555555;
$ud-label: #666666;
$ud-divider: #e2e2e2;
$ud-hover: #f5f5f5;
$ud-badge: #c8102e;
$ud-toggle-off: #1a1a1a;
$ud-toggle-on: #16c10e;
$ud-gutter: 24px;

.user-dropdown {
  width: 341px;
  max-width: calc(100vw - 32px);
  color: $ud-text;
  font-family: Arial, sans-serif;
  // vue-popper.css centers everything inside .popper
  text-align: left;

  p {
    margin: 0;
  }

  section,
  .links {
    border-bottom: 1px solid $ud-divider;
  }
}

.profile {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: $ud-gutter;

  .avatar {
    flex: 0 0 52px;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: $ud-text;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 700;
    object-fit: cover;
  }

  .identity {
    min-width: 0;
  }

  .name {
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    overflow-wrap: anywhere;
  }

  .email,
  .company {
    margin-top: 2px;
    font-size: 13px;
    line-height: 16px;
    color: $ud-muted;
    overflow-wrap: anywhere;
  }

  .role-badge {
    display: inline-block;
    margin-top: 8px;
    padding: 2px 8px;
    border: 1px solid $ud-badge;
    color: $ud-badge;
    font-size: 11px;
    font-weight: 700;
    line-height: 14px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
}

.toggles {
  padding: 16px $ud-gutter;

  .section-label {
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $ud-label;
  }

  ::v-deep .menu-toggle.form-check.form-switch {
    flex-direction: row-reverse;
    justify-content: space-between;
    min-height: 48px;
    margin: 0;
    padding: 0;

    .form-check-input {
      margin: 0;
      background-color: $ud-toggle-off;
      border-color: $ud-toggle-off;

      &:checked {
        background-color: $ud-toggle-on;
        border-color: $ud-toggle-on;
      }

      &:disabled {
        opacity: 0.5;
      }
    }

    .form-check-label {
      margin: 0;
      font-size: 15px;
      color: $ud-text;
      cursor: pointer;
    }
  }
}

.links {
  padding: 12px 0;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  a {
    display: flex;
    align-items: center;
    min-height: 45px;
    padding: 0 $ud-gutter;
    font-size: 15px;
    color: $ud-text;
    text-decoration: none;

    &:hover,
    &:focus-visible {
      background: $ud-hover;
    }
  }
}

.logout {
  padding: 12px 0;

  button {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 45px;
    padding: 0 $ud-gutter;
    border: 0;
    background: none;
    font-size: 14px;
    color: $ud-muted;
    text-align: left;

    &:hover,
    &:focus-visible {
      background: $ud-hover;
      color: $ud-text;
    }
  }
}
</style>
